import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { Expert, Vervolgstap } from '@common/customControls/bariatrics';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { addAllowNull } from '@common/validation/utils/addAllowNull';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export const bariPostLifestyleCoachReportVervolgstappen = [
    Vervolgstap.vervolgafspraak,
    Vervolgstap.expert_inschakelen,
    Vervolgstap.geen_actie_nodig,
] as const;

export const bariPostLifestyleCoachReportExperts = [
    Expert.psycholoog,
    Expert.dietist,
    Expert.verpleegkundig_specialist,
] as const;

export type BariPostLifestyleCoachReportVervolgstap = Extract<
    Vervolgstap,
    Vervolgstap.vervolgafspraak | Vervolgstap.expert_inschakelen | Vervolgstap.geen_actie_nodig
>;

export type BariPostLifestyleCoachReportData = {
    assets: ProcessActivityAsset[];
    expert: Expert | null;
    expert_toelichting: string | null;
    hulpvraag: string | null;
    interventie: string | null;
    verslag: string | null;
    vervolgstap: BariPostLifestyleCoachReportVervolgstap | null;
};

const bariPostLifestyleCoachReportFinal: TypedSchema<BariPostLifestyleCoachReportData> = {
    assets: getProcessActivityAssetsSchema(),
    expert: Koi.when('vervolgstap', {
        is: Vervolgstap.expert_inschakelen,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(Expert),
    }),
    expert_toelichting: Koi.when('expert', {
        is: null,
        otherwise: getTextAreaStringSchema(),
        then: getTextAreaStringSchema(true),
    }),
    hulpvraag: getTextAreaStringSchema(true),
    interventie: getTextAreaStringSchema(true),
    verslag: getTextAreaStringSchema(),
    vervolgstap: Koi.koi().valid(...bariPostLifestyleCoachReportVervolgstappen),
};

const bariPostLifestyleCoachReportDraft = addAllowNull(bariPostLifestyleCoachReportFinal, [
    'assets',
    'hulpvraag',
    'interventie',
]);

export const bariPostLifestyleCoachReportDraftSchema = Koi.object(bariPostLifestyleCoachReportDraft);
export const bariPostLifestyleCoachReportFinalSchema = Koi.object(bariPostLifestyleCoachReportFinal);

export type BariPostLifestyleCoachReportReadKey = keyof BariPostLifestyleCoachReportData;

export type BariPostLifestyleCoachReportReadLabel =
    | 'Bestand(en)'
    | 'Hulpvraag'
    | 'Interventie'
    | 'Verslag'
    | 'Vervolgstap'
    | 'Expert'
    | 'Toelichting';

export type BariPostLifestyleCoachReportReadSectionTitle = 'Verslag' | 'Vervolgstap';

export const bariPostLifestyleCoachReportReadSchema: TypedReadSchema<
    BariPostLifestyleCoachReportReadKey,
    BariPostLifestyleCoachReportReadLabel,
    BariPostLifestyleCoachReportReadSectionTitle
>[] = [
    {
        items: [
            {
                key: 'verslag',
                label: 'Verslag',
            },
            {
                key: 'hulpvraag',
                label: 'Hulpvraag',
            },
            {
                key: 'interventie',
                label: 'Interventie',
            },
            {
                key: 'assets',
                label: 'Bestand(en)',
            },
        ],
        sectionTitle: 'Verslag',
    },
    {
        items: [
            {
                key: 'vervolgstap',
                label: 'Vervolgstap',
            },
            {
                key: 'expert',
                label: 'Expert',
            },
            {
                key: 'expert_toelichting',
                label: 'Toelichting',
            },
        ],
        sectionTitle: 'Vervolgstap',
    },
];
